import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LANGUAGE_SWITCHING_ENABLED } from '../../i18n/config.js';
import { CONTACT_PATH } from '../../content/contacts.js';
import './Header.css';

const APP_URL = import.meta.env.VITE_APP_URL || 'http://localhost:3000';

/**
 * Public site header with logo, "Om oss" link, login link, and "Prøv gratis" button.
 * The language dropdown shows only when LANGUAGE_SWITCHING_ENABLED is true.
 * @returns {JSX.Element} Site header
 */
function Header() {
  const { t, i18n } = useTranslation();
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const dropdownRef = useRef(null);
  const activeLanguage = (i18n.resolvedLanguage || i18n.language || 'no').startsWith('en')
    ? 'en'
    : 'no';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    if (isLanguageOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageOpen]);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  return (
    <header className="site-header">
      <div className="site-header-content">
        <Link to="/" className="site-logo" aria-label={t('nav.home')}>
          <img src="/wordmark_navy.svg" alt="Revetall" className="site-logo-image" />
        </Link>

        <div className="site-header-actions">
          {LANGUAGE_SWITCHING_ENABLED && (
            <div className="language-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="language-dropdown-toggle"
                onClick={() => setIsLanguageOpen((open) => !open)}
                aria-expanded={isLanguageOpen}
                aria-haspopup="listbox"
              >
                {t('nav.language')}
              </button>
              {isLanguageOpen && (
                <div className="language-dropdown-menu" role="listbox">
                  <button
                    type="button"
                    className={`language-dropdown-item ${activeLanguage === 'no' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('no')}
                  >
                    {t('nav.norwegian')} {activeLanguage === 'no' ? '✓' : ''}
                  </button>
                  <button
                    type="button"
                    className={`language-dropdown-item ${activeLanguage === 'en' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('en')}
                  >
                    {t('nav.english')} {activeLanguage === 'en' ? '✓' : ''}
                  </button>
                </div>
              )}
            </div>
          )}

          <Link to="/om-oss" className="header-link">
            {t('nav.about')}
          </Link>
          <a href={APP_URL} className="login-button">
            {t('nav.login')}
          </a>
          <Link to={CONTACT_PATH} className="button button-primary header-cta">
            {t('nav.contact')}
          </Link>
        </div>
      </div>
    </header>
  );
}

export default Header;
